export interface AdminUser {
  id: string;
  username: string;
  email: string;
  role: 'admin' | 'super_admin';
  createdAt: string;
  updatedAt: string;
}

export interface EventSession {
  id: string;
  eventName: string;
  location: string;
  startTime: string;
  endTime: string;
  status: 'active' | 'inactive' | 'ended';
  qrDisplayUrl: string;
  totpSecretKey: string;
  description?: string;
  qrRefreshInterval: number;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEventRequest {
  eventName: string;
  location: string;
  startTime: string;
  endTime: string;
  description?: string;
  qrRefreshInterval?: number;
}

export interface UpdateEventRequest extends Partial<CreateEventRequest> {
  status?: 'active' | 'inactive' | 'ended';
}

export interface MiniQuizQuestion {
  id: number;
  imageUrl: string;
  question: string;
  correctAnswer: boolean;
  explanation?: string;
  isActive: boolean;
  category?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GetQuestionsParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  category?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'question';
  sortOrder?: 'asc' | 'desc';
}

export interface MiniQuizFilters {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  category?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'question';
  sortOrder?: 'asc' | 'desc';
}

export interface MiniQuizResponse {
  questions: MiniQuizQuestion[];
  total: number;
  page: number;
  limit: number;
}

export interface MiniQuizStatistics {
  overview: {
    totalQuestions: number;
    activeQuestions: number;
    inactiveQuestions: number;
  };
  recentActivity: Array<{
    date: string;
    newQuestions: number;
    activatedQuestions: number;
  }>;
}

// Quiz Categories
export const QUIZ_CATEGORIES = ['30nam', 'megasale', 'myshop', 'tindung', 'xdigi'] as const;
export type QuizCategory = typeof QUIZ_CATEGORIES[number];

export interface MiniQuizFormData {
  imageUrl: string;
  question: string;
  correctAnswer: boolean;
  explanation?: string;
  isActive?: boolean;
  category?: string;
}

export interface DashboardStats {
  totalUsers: number;
  activeGames: number;
  totalScores: number;
  revenue: number;
}

export interface User {
  id: string;
  name: string;
  username?: string; // for backward compatibility
  email: string | null;
  phone: string | null;
  level?: number;
  experience?: number;
  coins?: number;
  energy: number;
  isAppUser: boolean;
  phoneVerified: boolean;
  province: string | null;
  ward: string | null;
  gender: string | null;
  birthYear: number | null;
  refBy: string | null;
  status: 'active' | 'suspended' | 'banned' | 'unverified';
  role: 'user' | 'premium_user' | 'moderator' | 'app_user' | 'web_user';
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  profile?: {
    firstName?: string;
    lastName?: string;
    avatar?: string;
    bio?: string;
    country?: string;
  };
  stats?: {
    totalGamesPlayed: number;
    totalScore: number;
    achievementsCount: number;
    streakDays: number;
  };
}

export interface CreateUserData {
  username: string;
  email: string;
  password: string;
  role?: 'user' | 'premium_user' | 'moderator';
  profile?: {
    firstName?: string;
    lastName?: string;
    bio?: string;
    country?: string;
  };
}

export interface UpdateUserData {
  username?: string;
  email?: string;
  status?: 'active' | 'suspended' | 'banned';
  role?: 'user' | 'premium_user' | 'moderator';
  profile?: {
    firstName?: string;
    lastName?: string;
    bio?: string;
    country?: string;
  };
}

export interface UserFilters {
  status?: string;
  role?: string;
  search?: string;
}

export interface UserManagement {
  id: string;
  username: string;
  email: string;
  level: number;
  experience: number;
  coins: number;
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GameManagement {
  id: string;
  name: string;
  description: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  thumbnail: string;
  isActive: boolean;
  playCount: number;
  averageScore: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
  };
}

export interface UsersApiResponse {
  success: boolean;
  data: {
    users: User[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalUsers: number;
      hasNext: boolean;
      hasPrev: boolean;
      limit: number;
    };
  };
  message: string;
}

export enum VoucherType {
  VOUCHER_10K = '10k',
  VOUCHER_20K = '20k',
  VOUCHER_50K = '50k',
  VOUCHER_100K = '100k',
  VOUCHER_200K = '200k',
  VOUCHER_300K = '300k',
  VOUCHER_500K = '500k',
  VOUCHER_1M = '1m',
  VOUCHER_2M = '2m'
}

export enum VoucherStatus {
  AVAILABLE = 'available',
  CLAIMED = 'claimed',
  USED = 'used',
  EXPIRED = 'expired'
}

export interface Voucher {
  id: string;
  code: string;
  voucher_type: VoucherType;
  value: number;
  status: VoucherStatus;
  user_id?: string;
  user?: {
    id: string;
    name: string;
    phone: string | null;
    email: string | null;
  };
  claimed_at?: string;
  used_at?: string;
  expires_at?: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface VoucherStatistics {
  overview: {
    totalVouchers: number;
    availableVouchers: number;
    claimedVouchers: number;
    usedVouchers: number;
    expiredVouchers: number;
    totalValue: number;
  };
  byType: {
    [key in VoucherType]: {
      count: number;
      totalValue: number;
      availableCount: number;
      claimedCount: number;
      usedCount: number;
    };
  };
}

export interface VouchersApiResponse {
  success: boolean;
  data: {
    vouchers: Voucher[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalVouchers: number;
      hasNext: boolean;
      hasPrev: boolean;
      limit: number;
    };
    summary: {
      totalAvailable: number;
      totalClaimed: number;
      totalUsed: number;
      totalExpired: number;
      totalValue: number;
    };
  };
  message: string;
}

export interface VoucherFormData {
  code: string;
  voucher_type: VoucherType;
  value: number;
  description?: string;
  expires_at?: string;
}
